// ---------------------------------------------------------------------------
// Permission types
// ---------------------------------------------------------------------------
//
// The server is the single source of truth for what a user can do with an
// entity. Every entity-bearing response carries an `access.capabilities`
// object computed by `services::audience::AccessPayload::to_json` from the
// resolved `PermissionLevel`. The client reads those booleans directly to
// gate UI — there is NO client-side ladder derivation. Adding/removing
// gates is a server-side change.

export type Permission = 'read' | 'edit' | 'admin' | 'owner';

export type AccessVia = 'direct' | 'collection' | 'workspace' | 'creator' | 'workspace_grant'

export function accessViaLabel(via: AccessVia): string {
  switch (via) {
    case 'direct': return 'Shared directly'
    case 'collection': return 'Inherited from a collection'
    case 'workspace': return 'Workspace member'
    case 'creator': return 'Created by you'
    case 'workspace_grant': return 'Shared with another workspace'
    default: {
      // Exhaustiveness guard: adding a new AccessVia variant without updating
      // this switch becomes a compile error rather than a silent `undefined`.
      const _exhaustive: never = via
      return _exhaustive
    }
  }
}

export interface GrantedBy {
  userId: string;
  name: string;
  email?: string | null;
  avatarUrl: string | null;
}

/// Per-entity capability flags computed by the server. The client gates UI
/// by reading these booleans — never by re-deriving from `permission`.
export interface Capabilities {
  view: boolean;
  edit: boolean;
  manage: boolean;
  delete: boolean;
}

export interface Access {
  permission: Permission;
  grantedAt: string;
  grantedBy: GrantedBy | null; // null when permission === 'owner' or (future) via === 'workspace'
  via: AccessVia;
  capabilities: Capabilities;
  /** Whether this user may remove their own access. Server-computed from the
   *  existence of a direct grant row — NOT derivable from `via`, which reports
   *  which permission path won precedence. */
  removeSelf: boolean;
  /** For collections only: how many contained items this user would lose. */
  removeSelfItemCount: number | null;
}

// ---------------------------------------------------------------------------
// Permission display labels (string-keyed for legacy callers — Permission keys)
// ---------------------------------------------------------------------------

export const PERMISSION_LABEL: Record<string, string> = {
  read: 'Read only',
  edit: 'Can edit',
  admin: 'Admin',
  owner: 'Owner',
};

// ---------------------------------------------------------------------------
// Capability helpers — server-driven
// ---------------------------------------------------------------------------
//
// Tolerant of payloads from servers that don't yet send `capabilities`: in
// that transitional case we derive from `permission` using the same truth
// table the server applies (Capabilities::from_level in Rust). Once every
// in-flight payload has capabilities, the fallback is dead code.

function capsOrFallback(access: Access): Capabilities {
  if (access.capabilities) return access.capabilities;
  const p = access.permission;
  return {
    view: true,
    edit: p === 'edit' || p === 'admin' || p === 'owner',
    manage: p === 'admin' || p === 'owner',
    delete: p === 'admin' || p === 'owner',
  };
}

export const canView = (access: Access): boolean => capsOrFallback(access).view;
export const canEdit = (access: Access): boolean => capsOrFallback(access).edit;
export const canManage = (access: Access): boolean => capsOrFallback(access).manage;
export const canDelete = (access: Access): boolean => capsOrFallback(access).delete;

// The `=== true` guard is load-bearing: fallback payloads (kind-level branches
// in the server that hand-build an access object) omit this key entirely, and
// `undefined` must read as `false`, never as truthy-undefined.
export const canRemoveSelf = (access: Access): boolean => access.removeSelf === true;

// ---------------------------------------------------------------------------
// Ownership (not a policy gate — a "did I create this?" question for UI)
// ---------------------------------------------------------------------------

export const isOwned = (r: { access: Access }): boolean =>
  r.access.permission === 'owner';

export const isSharedWithMe = (r: { access: Access }): boolean => !isOwned(r);
